import JSZip from "jszip"
import { useEffect, useRef, useState } from "react"
import { extractProperty, getUmbracoMediaUrl } from "utils"

type DownloadProgress = {
  isDownloading: boolean
  currentFile: number
  totalFiles: number
  currentFileName: string
  progress: number
  success: string
}

export const useFileDownload = () => {
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress>({
    isDownloading: false,
    currentFile: 0,
    totalFiles: 0,
    currentFileName: "",
    progress: 0,
    success: "",
  })

  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const resetProgress = () => {
    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    setDownloadProgress({
      isDownloading: false,
      currentFile: 0,
      totalFiles: 0,
      currentFileName: "",
      progress: 0,
      success: "",
    })
  }

  const generateUniqueFileName = (fileName: string, existingNames: string[]) => {
    let uniqueFileName = fileName
    let counter = 1
    while (existingNames.includes(uniqueFileName)) {
      const extension = fileName.includes(".") ? fileName.split(".").pop() : ""
      const baseName = fileName.includes(".") ? fileName.replace(`.${extension}`, "") : fileName
      uniqueFileName = extension ? `${baseName}_${counter}.${extension}` : `${baseName}_${counter}`
      counter++
    }
    return uniqueFileName
  }

  const processFileName = (file: any, index: number, transformedUrl: string | null) => {
    let fileName =
      extractProperty(file, "fileName") ||
      extractProperty(file, "name") ||
      `file_${file.id || Date.now()}_${index}`

    // Extract extension from URL if fileName doesn't have one
    if (!fileName.includes(".") && transformedUrl) {
      const urlParts = transformedUrl.split("/")
      const lastPart = urlParts[urlParts.length - 1]
      if (lastPart.includes(".")) {
        const extension = lastPart.split(".").pop()
        fileName = `${fileName}.${extension}`
      }
    }

    return fileName
  }

  const downloadFile = async (transformedUrl: string, fileName: string) => {
    try {
      // Use the proxy API to avoid CORS issues
      const proxyUrl = `/api/download?url=${encodeURIComponent(transformedUrl)}`

      const response = await fetch(proxyUrl, {
        method: "GET",
        headers: {
          Accept: "*/*",
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const blob = await response.blob()
      return blob
    } catch (error) {
      console.error(`Error downloading file ${fileName}:`, error)
      return null
    }
  }

  const downloadAsZip = async (files: any[]) => {
    try {
      setDownloadProgress({
        isDownloading: true,
        currentFile: 0,
        totalFiles: files.length,
        currentFileName: "",
        progress: 0,
        success: "",
      })

      const zip = new JSZip()

      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        const uploadFile = extractProperty(file, "uploadFile")

        const transformedUrl = getUmbracoMediaUrl(uploadFile)

        const fileName = processFileName(file, i, transformedUrl)

        // Update progress with current file info
        setDownloadProgress((prev) => ({
          ...prev,
          currentFile: i + 1,
          currentFileName: fileName,
          progress: Math.round(((i + 1) / files.length) * 100),
        }))

        if (transformedUrl) {
          const blob = await downloadFile(transformedUrl, fileName)
          if (blob) {
            // Ensure unique filenames to avoid overwriting
            const existingNames = zip.file(/./)?.map((f) => f.name) || []
            const uniqueFileName = generateUniqueFileName(fileName, existingNames)
            zip.file(uniqueFileName, blob)
          }
        } else {
          console.warn(`No transformed URL for file ${i + 1}:`, file)
        }
      }

      // Generate and download the ZIP file
      const zipBlob = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: {
          level: 6,
        },
      })

      const url = window.URL.createObjectURL(zipBlob)
      const link = document.createElement("a")
      link.href = url
      link.download = `documents_${new Date().toISOString().split("T")[0]}.zip`
      link.style.display = "none"
      document.body.appendChild(link)
      link.click()

      // Cleanup after a delay
      timeoutRef.current = setTimeout(() => {
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        setDownloadProgress({
          isDownloading: false,
          currentFile: 0,
          totalFiles: 0,
          currentFileName: "",
          progress: 100,
          success: "successed",
        })
        timeoutRef.current = null
      }, 100)
    } catch (error) {
      console.error("Error creating ZIP file:", error)
      setDownloadProgress({
        isDownloading: false,
        currentFile: 0,
        totalFiles: 0,
        currentFileName: "",
        progress: 0,
        success: "failed",
      })
    }
  }

  return {
    downloadProgress,
    downloadAsZip,
    resetProgress,
  }
}
